import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  createRequestApi,
  createOfferApi,
  updateRequestApi,
  updateOfferApi,
  patchRequestApi,
  patchOfferApi,
  deleteRequestApi,
  deleteOfferApi,
  deleteRequestsApi,
  deleteOffersApi,
  uploadRequestFile,
  uploadOfferFile,
  completeRequestApi,
} from '../api/requestApi'
import { type Request, type Offer } from '../utils/types'
import LogTool from '../../../logger/logTools'
import { useSmartPatchMutation } from '../../../api/hooks/useSmartMutations'
import { requestKeys, offerKeys } from '../utils/queryKeys'
import { articleKeys } from '../../Item/utils/queryKeys'
import { bomKeys } from '../../Bom/utils/queryKeys'
import { getPKfromSelf } from '../../../utils/functions'
import { APIPart, APIPartUpdate } from '../../../generated-types'
import { RecalculateRequestPartInput, UpdateRequestPartInput, recalculateRequestPartApi, updateRequestPartApi } from '../api/requestPartApi'

const log = new LogTool({ context: 'RequestMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a new request
 */
export function useCreateRequest(): UseMutationResult<Request, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createRequestApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: requestKeys.lists() })
      log.info('Request created successfully')
    },
    onError: (error) => {
      log.error('Error creating request:', error)
    },
  })
}

/**
 * Hook to create a new offer
 */
export function useCreateOffer(): UseMutationResult<Offer, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createOfferApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: offerKeys.lists() })
      log.info('Offer created successfully')
    },
    onError: (error) => {
      log.error('Error creating offer:', error)
    },
  })
}

/**
 * Hook to update a request (full update with PUT)
 */
export function useUpdateRequest(): UseMutationResult<
  Request,
  Error,
  { idOrSelf: string | number; data: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateRequestApi(idOrSelf, data),
    onSuccess: async (updatedRequest) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: requestKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: Request) =>
              item.key === updatedRequest.key ? updatedRequest : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data (editors, files, article, etc.)
      await queryClient.invalidateQueries({ 
        queryKey: requestKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: requestKeys.details(Number(getPKfromSelf(updatedRequest.self)))
      })
      log.info('Request updated successfully')
    },
    onError: (error) => {
      log.error('Error updating request:', error)
    },
  })
}

/**
 * Hook to update an offer (full update with PUT)
 */
export function useUpdateOffer(): UseMutationResult<
  Offer,
  Error,
  { idOrSelf: string | number; data: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateOfferApi(idOrSelf, data),
    onSuccess: async (updatedOffer) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: offerKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: Offer) =>
              item.key === updatedOffer.key ? updatedOffer : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data (editors, files, request, etc.)
      await queryClient.invalidateQueries({ 
        queryKey: offerKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({ 
        queryKey: offerKeys.details(Number(getPKfromSelf(updatedOffer.self)))
      })
      log.info('Offer updated successfully')
    },
    onError: (error) => {
      log.error('Error updating offer:', error)
    },
  })
}

/**
 * Hook to patch a request (partial update with PATCH)
 */
export function usePatchRequest(): UseMutationResult<
  Request,
  Error,
  { idOrSelf: string | number; data: Partial<any> }
> {
  const queryClient = useQueryClient()

  return useSmartPatchMutation({
    queryKeyFactory: requestKeys,
    mutationFn: ({ idOrSelf, data }) => patchRequestApi(idOrSelf, data),
    onSuccess: (updated) => {
      log.info('Success patching request:', updated.key)
    },
    onError: (error) => {
      log.error('Error patching request:', error)
    },
  }, queryClient)
}

/**
 * Hook to patch an offer (partial update with PATCH)
 */
export function usePatchOffer(): UseMutationResult<
  Offer,
  Error,
  { idOrSelf: string | number; data: Partial<any> }
> {
  const queryClient = useQueryClient()

  return useSmartPatchMutation({
    queryKeyFactory: offerKeys,
    mutationFn: ({ idOrSelf, data }) => patchOfferApi(idOrSelf, data),
    onSuccess: (updated) => {
      log.info('Success patching offer:', updated.key)
    },
    onError: (error) => {
      log.error('Error patching offer:', error)
    },
  }, queryClient)
}

/**
 * Hook to delete a single request
 */
export function useDeleteRequest(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteRequestApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: requestKeys.lists() })
      // Also invalidate article and BOM queries since they may no longer be visible
      // to staff users after the request is deleted
      await queryClient.invalidateQueries({ queryKey: articleKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: bomKeys.lists() })
      log.info('Request deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting request:', error)
    },
  })
}

/**
 * Hook to delete a single offer
 */
export function useDeleteOffer(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteOfferApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: offerKeys.lists() })
      log.info('Offer deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting offer:', error)
    },
  })
}

/**
 * Hook to delete multiple requests
 */
export function useDeleteRequests(): UseMutationResult<void, Error, Request[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteRequestsApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: requestKeys.lists() })
      // Also invalidate article and BOM queries since they may no longer be visible
      // to staff users after the requests are deleted
      await queryClient.invalidateQueries({ queryKey: articleKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: bomKeys.lists() })
      log.info('Requests deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting requests:', error)
    },
  })
}

/**
 * Hook to delete multiple offers
 */
export function useDeleteOffers(): UseMutationResult<void, Error, Offer[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteOffersApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: offerKeys.lists() })
      log.info('Offers deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting offers:', error)
    },
  })
}

/**
 * Hook to upload file to AQG request
 */
export function useUploadRequestFile(): UseMutationResult<
  void,
  Error,
  { requestSelf: string; file: File }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ requestSelf, file }) => uploadRequestFile(requestSelf, file),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ 
        queryKey: requestKeys.details(Number(getPKfromSelf(variables.requestSelf)))
      })
      await queryClient.invalidateQueries({ 
        queryKey: [...requestKeys.all, 'files', variables.requestSelf]
      })
      log.info('File uploaded to AQG request successfully')
    },
    onError: (error) => {
      log.error('Error uploading file to AQG request:', error)
    },
  })
}

/**
 * Hook to upload file to offer
 */
export function useUploadOfferFile(): UseMutationResult<
  void,
  Error,
  { offerSelf: string; file: File }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ offerSelf, file }) => uploadOfferFile(offerSelf, file),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ 
        queryKey: offerKeys.details(Number(getPKfromSelf(variables.offerSelf)))
      })
      log.info('File uploaded to offer successfully')
    },
    onError: (error) => {
      log.error('Error uploading file to offer:', error)
    },
  })
}

/**
 * Hook to complete a temporary AQG request
 */
export function useCompleteRequest(): UseMutationResult<
  Request,
  Error,
  { idOrSelf: string | number; data: any; accessSecret?: string; expand?: string[] }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data, accessSecret, expand }) => 
      completeRequestApi(idOrSelf, data, accessSecret, expand),
    onSuccess: async (completedRequest) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: requestKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: Request) =>
              item.key === completedRequest.key ? completedRequest : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data (editors, files, etc.)
      await queryClient.invalidateQueries({ 
        queryKey: requestKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({
        queryKey: requestKeys.detail(Number(getPKfromSelf(completedRequest.self))),
      })
      log.info('AQG request completed successfully')
    },
    onError: (error) => {
      log.error('Error completing AQG request:', error)
    },
  })
}

/**
 * Hook to recalculate a AQG request part price
 */
export function useRecalculateAQGRequestPart(): UseMutationResult<
  APIPart,
  Error,
  RecalculateRequestPartInput
> {
  return useMutation({
    mutationFn: recalculateRequestPartApi,
    onSuccess: () => {
      log.info('AQG request part recalculated successfully')
    },
    onError: (error) => {
      log.error('Error recalculating AQG request part:', error)
    },
  })
}

/**
 * Hook to update a AQG request part
 */
export function useUpdateAQGRequestPart(): UseMutationResult<
  APIPartUpdate,
  Error,
  UpdateRequestPartInput
> {
  return useMutation({
    mutationFn: updateRequestPartApi,
    onSuccess: () => {
      log.info('AQG request part updated successfully')
    },
    onError: (error) => {
      log.error('Error updating AQG request part:', error)
    },
  })
}